import { Lightning, Log } from '@lightningjs/sdk'
import VideoTexture from '../lib/components/video-texture'
import { ContentPlayer } from '../lib/components/ft-streaming-player/content-player'

export interface VideoPreviewTextureTemplateSpec extends Lightning.Component.TemplateSpecLoose {
    VideoTextureWrapper: typeof VideoTexture
}

/**
 * VideoPreviewTexture Component
 * 
 * Renders video previews using VideoTexture component for mid-carousel thumbnails.
 * Handles lifecycle: creation, playback, and cleanup on focus/blur events.
 * 
 * NOTE: This component does NOT manage video source - it only renders the texture.
 * The Autoplay service (via ContentPlayer) manages video loading and playback.
 * This component simply attaches to the shared video element for rendering.
 * 
 * Key Features:
 * - Uses VideoTexture with WebGL texture rendering
 * - Attaches to shared ContentPlayer video element
 * - Efficient requestVideoFrameCallback for smooth playback
 * - Automatic cleanup on blur
 * - 300ms focus debounce to prevent scroll churn
 * - Fade-in animation on load
 */
export default class VideoPreviewTexture
    extends Lightning.Component<VideoPreviewTextureTemplateSpec>
    implements Lightning.Component<VideoPreviewTextureTemplateSpec> {
    
    VideoTextureWrapper!: Lightning.Element
    
    private _isPlaying: boolean = false
    private _focusDebounceTimer: number | null = null
    private _videoElement: HTMLVideoElement | null = null
    private _videoTextureComponent: any = null
    
    static readonly FOCUS_DEBOUNCE_MS = 300
    static readonly FADE_IN_DURATION = 0.4
    
    /**
     * Handle video canplay event - start playback
     * @private
     */

    
    static override _template(): Lightning.Component.Template<VideoPreviewTextureTemplateSpec> {
        return {
            w: (w: number) => w,
            h: (h: number) => h,

        }
    }

    /**
     * Start video preview with debounce
     * Called when the thumbnail gains focus
     * Video source is managed by Autoplay service
     */
    startPreview(): void {
        Log.info('VideoPreviewTexture: startPreview called', {
            isPlaying: this._isPlaying,
            debounceMs: VideoPreviewTexture.FOCUS_DEBOUNCE_MS
        })
        

        this._clearDebounceTimer()
        

        Log.info('VideoPreviewTexture: Starting debounce timer for ' + VideoPreviewTexture.FOCUS_DEBOUNCE_MS + 'ms')
        this._focusDebounceTimer = window.setTimeout(() => {
            Log.info('VideoPreviewTexture: Debounce timer fired, initializing video')
            this._initVideoTexture()
            this._focusDebounceTimer = null
        }, VideoPreviewTexture.FOCUS_DEBOUNCE_MS)
    }

    /**
     * Stop video preview immediately
     * Called when the thumbnail loses focus
     */
    stopPreview(): void {
        // Early return if nothing to stop
        if (!this._videoElement && !this._isPlaying && !this._focusDebounceTimer) {
            return
        }
        Log.info('VideoPreviewTexture: stopPreview called', {
            isPlaying: this._isPlaying,
            hasVideoElement: !!this._videoElement
        })
        this._clearDebounceTimer()
        this._destroyVideoTexture()
    }

    /**
     * Initialize HTML5 video element and assign to VideoTexture
     * Attaches to the shared ContentPlayer video element managed by Autoplay service
     * @private
     */
    private _initVideoTexture(): void {
        Log.info('VideoPreviewTexture: _initVideoTexture called', {
            isPlaying: this._isPlaying
        })
        

        try {
            Log.info('VideoPreviewTexture: Attaching to shared ContentPlayer')


            if (!this._videoTextureComponent) {
                this._videoTextureComponent = this.stage.c({
                    type: VideoTexture,
                    x: 0,
                    y: 0,
                    w: this.finalW,
                    h: this.finalH,
                    alpha: 0,
                    zIndex: 2
                })
                this.childList.add(this._videoTextureComponent)
                Log.info('VideoPreviewTexture: VideoTexture component created dynamically')
            }


            const contentPlayer = ContentPlayer.getInstance()
            this._videoElement = contentPlayer.getVideoEl()

            if (!this._videoElement) {
                 Log.error('VideoPreviewTexture: Shared video element not available')
                 return
            }


            this._videoTextureComponent.videoEl = this._videoElement
            

            this._videoTextureComponent._createVideoTexture()
            Log.info('VideoPreviewTexture: VideoTexture initialized with shared video element')
            

            const videoView = this._videoTextureComponent.tag('Video')
            if (videoView) {
                videoView.w = this.finalW
                videoView.h = this.finalH
            }


            this._videoTextureComponent.start()
            this._isPlaying = true
            

            this._fadeIn()

        } catch (error: any) {
            Log.error('VideoPreviewTexture: Failed to initialize video element', error)
            this._destroyVideoTexture()
        }
    }

    /**
     * Fade in the video texture
     * @private
     */
    private _fadeIn(): void {
        Log.info('VideoPreviewTexture: _fadeIn called')
        if (this._videoTextureComponent) {
            this._videoTextureComponent.setSmooth('alpha', 1, {
                duration: VideoPreviewTexture.FADE_IN_DURATION,
                timingFunction: 'ease-out'
            })
            Log.info('VideoPreviewTexture: Fade-in animation started')
        } else {
            Log.error('VideoPreviewTexture: Cannot fade in, VideoTexture component not found')
        }
    }

    /**
     * Destroy the video texture and clear resources
     * @private
     */
    private _destroyVideoTexture(): void {
        // Early return if nothing to destroy
        if (!this._videoElement && !this._videoTextureComponent && !this._isPlaying) {
            return
        }

        Log.info('VideoPreviewTexture: Destroying video texture', {
            hasVideoElement: !!this._videoElement,
            isPlaying: this._isPlaying
        })

        if (!this._videoElement) {
            Log.info('VideoPreviewTexture: No video element to destroy')
            this._isPlaying = false
            return
        }

        try {

            if (this._videoTextureComponent) {
                this._videoTextureComponent.stop()
                this._videoTextureComponent.hide()
                Log.info('VideoPreviewTexture: VideoTexture stopped and hidden')
            }


            if (this._videoElement) {

                
                this._videoElement = null
                Log.info('VideoPreviewTexture: Shared video element disconnected')
            }


            if (this._videoTextureComponent) {

                this._videoTextureComponent.videoEl = null
                
                this.childList.remove(this._videoTextureComponent)
                this._videoTextureComponent = null
                Log.info('VideoPreviewTexture: VideoTexture component removed')
            }

            this._isPlaying = false

        } catch (error: any) {
            Log.error('VideoPreviewTexture: Error destroying video texture', error)
        }
    }

    /**
     * Clear the focus debounce timer
     * @private
     */
    private _clearDebounceTimer(): void {
        if (this._focusDebounceTimer !== null) {
            clearTimeout(this._focusDebounceTimer)
            this._focusDebounceTimer = null
        }
    }

    /**
     * Cleanup when component is detached
     * @override
     */
    override _detach(): void {
        this._clearDebounceTimer()
        // Only destroy if there's actually something to clean up
        if (this._videoElement || this._videoTextureComponent || this._isPlaying) {
            this._destroyVideoTexture()
        }
        super._detach()
    }

    /**
     * Check if video is currently playing
     */
    get isPlaying(): boolean {
        return this._isPlaying
    }
}
